import React, { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// Constants for navigation types from the Performance API
const PERFORMANCE_NAVIGATION_TYPE_NAVIGATE = "navigate";
const PERFORMANCE_NAVIGATION_TYPE_RELOAD = "reload";
const PERFORMANCE_NAVIGATION_TYPE_BACK_FORWARD = "back_forward";
const PERFORMANCE_NAVIGATION_TYPE_POP = "pop";
const PERFORMANCE_NAVIGATION_TYPE_PUSH = "push";
const PERFORMANCE_NAVIGATION_TYPE_PRERENDER = "prerender";

// Type definition for navigation types
type PerformanceNavigationType =
  | typeof PERFORMANCE_NAVIGATION_TYPE_NAVIGATE
  | typeof PERFORMANCE_NAVIGATION_TYPE_RELOAD
  | typeof PERFORMANCE_NAVIGATION_TYPE_BACK_FORWARD
  | typeof PERFORMANCE_NAVIGATION_TYPE_POP
  | typeof PERFORMANCE_NAVIGATION_TYPE_PUSH
  | typeof PERFORMANCE_NAVIGATION_TYPE_PRERENDER;

// Interface for history state entries with path and browser-generated key
interface HistoryStateWithPath {
  path: string;
  key: string | null | undefined; // key of the history state generated by the browser
}

// Interface for the navigation context value
interface NavigationContextValue {
  trackNavigation: (to: string | number, options: { replace?: boolean }) => void;
  goBack: () => void;
  goForward: () => void;
  canGoBack: boolean;
  canGoForward: boolean;
}

// Create context with null as default value
const NavigationContext = createContext<NavigationContextValue | null>(null);

interface NavigationProviderProps {
  children: ReactNode;
}

// Session storage keys for persisting navigation state
const STORAGE_KEYS = {
  APP_HISTORY: "appNavHistory",
  CURRENT_INDEX: "appNavCurrentIndex",
};

export const NavigationProvider: React.FC<NavigationProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Main state for tracking navigation history and current position
  const [appHistory, setAppHistory] = useState<HistoryStateWithPath[]>([]);
  const [currentIndex, setCurrentIndex] = useState<number>(0);

  // Refs to access current state values in event listeners
  const appHistoryRef = useRef<HistoryStateWithPath[]>(appHistory);
  const currentIndexRef = useRef<number>(currentIndex);

  // Flag to prevent re-initialization
  const isInitialized = useRef(false);

  // Commented out code removed for clarity

  useEffect(() => {
    // 1. Initialize the appHistory and currentIndex based on page load method
    // ----------------------------------------------------------------------------------------
    if (isInitialized.current) {
      return;
    }

    const currentPath = location.pathname + location.search;
    const historyStateKey = window.history.state?.key;

    // Detect how the page was loaded (direct navigation, refresh, or back/forward)
    let navigationType: PerformanceNavigationType | null = null;
    if (typeof window !== "undefined" && window.performance) {
      const navigation: PerformanceNavigationTiming[] = window.performance.getEntriesByType(
        "navigation",
      ) as PerformanceNavigationTiming[];
      navigationType = navigation.length > 0 ? navigation[0].type : null;
    }

    // Try to restore history from session storage
    const storedHistory = sessionStorage.getItem(STORAGE_KEYS.APP_HISTORY) ?? "[]";
    const storedIndex = sessionStorage.getItem(STORAGE_KEYS.CURRENT_INDEX) ?? 0;

    let parsedHistory: HistoryStateWithPath[] = [];
    let parsedIndex = 0;

    try {
      // Parse stored history but reset keys since they're specific to browser sessions
      parsedHistory = JSON.parse(storedHistory)?.map((item: HistoryStateWithPath) => ({
        path: item.path,
        key: null, // new browser session so the old keys won't work
      }));
      parsedIndex = storedIndex ? parseInt(storedIndex, 10) : parsedHistory.length - 1;
    } catch (error) {
      console.error("[Initial Render] Error parsing storedHistory", error);
    }

    // For direct navigation or page refresh, initialize with current path only
    if (
      [PERFORMANCE_NAVIGATION_TYPE_NAVIGATE, PERFORMANCE_NAVIGATION_TYPE_RELOAD].includes(
        navigationType as PerformanceNavigationType,
      )
    ) {
      parsedHistory = [{ path: currentPath, key: historyStateKey }];
      parsedIndex = 0;
    } else {
      // For back/forward navigation, use stored history but ensure current path is included
      if (parsedHistory[parsedHistory.length - 1].path !== currentPath) {
        parsedHistory.push({ path: currentPath, key: historyStateKey });
        parsedIndex = parsedHistory.length - 1;
      }
    }

    // Update state and persist to session storage
    setAppHistory(parsedHistory);
    setCurrentIndex(parsedIndex);
    sessionStorage.setItem(STORAGE_KEYS.APP_HISTORY, JSON.stringify(parsedHistory));
    sessionStorage.setItem(STORAGE_KEYS.CURRENT_INDEX, parsedIndex.toString());

    isInitialized.current = true;

    // Only run once on initial render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // 2. Handle browser back/forward navigation events
    // ----------------------------------------------------------------------------------------
    const handlePopState = (state: PopStateEvent) => {
      // This callback runs AFTER the browser has already navigated to a new history entry
      // We need to update our internal state (currentIndex) to match the browser's state

      const location = window.location; // Important: use window.location in event handlers
      const currentPath = location.pathname + location.search;
      const historyStateKey = state.state?.key;

      // Strategy 1: Check if user navigated FORWARD in history
      // Look for matching path AND key in the forward portion of our history
      const findNextForwardPath = appHistoryRef.current
        .slice(currentIndexRef.current + 1)
        .findIndex((appHistoryItem) => appHistoryItem.path === currentPath && appHistoryItem.key === historyStateKey);

      if (findNextForwardPath > -1) {
        // Found a match in forward history - update currentIndex accordingly
        setCurrentIndex(currentIndexRef.current + 1 + findNextForwardPath);
        return;
      }

      // Strategy 2: Check if user navigated BACKWARD in history
      // For backward navigation, we only match by path since keys might be null
      // from session storage restoration
      const findNextBackwardPath = appHistoryRef.current
        .slice(0, currentIndexRef.current)
        .reverse()
        .findIndex((appHistoryItem) => appHistoryItem.path === currentPath);

      if (findNextBackwardPath > -1) {
        // Found a match in backward history - update currentIndex and store the key
        const newIndex = currentIndexRef.current - findNextBackwardPath - 1;
        appHistoryRef.current[newIndex].key = historyStateKey; // Update key for future matching
        setCurrentIndex(newIndex);
        return;
      }

      // Fallback: If we can't determine the navigation direction,
      // treat it as a new navigation and add to history
      const newHistory = [
        ...appHistory.slice(0, currentIndexRef.current + 1),
        { path: currentPath, key: historyStateKey },
      ];
      setAppHistory(newHistory);
      setCurrentIndex(newHistory.length - 1);
    };

    // Register and clean up the popstate event listener
    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
    // Only run once on initial render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // 3. Keep refs updated for use in event handlers
    // ----------------------------------------------------------------------------------------
    // This ensures our popstate handler always has access to the latest state values
    appHistoryRef.current = appHistory;
    currentIndexRef.current = currentIndex;
  }, [appHistory, currentIndex]);

  useEffect(() => {
    // 4. Update history state keys after location changes
    // ----------------------------------------------------------------------------------------
    // After a navigation occurs, the browser assigns a history state key
    // We need to capture this key and store it with our history entry

    if (!isInitialized.current) {
      return;
    }

    const currentPath = location.pathname + location.search;
    // Only update if the current index points to the current path
    if (!appHistory[currentIndex] || appHistory[currentIndex].path !== currentPath) {
      return;
    }

    // Update the history entry with the browser-generated key
    setAppHistory((prevHistory) => {
      const updatedHistory = [...prevHistory]; // Create a copy to maintain immutability
      updatedHistory[currentIndex] = {
        path: currentPath,
        key: window.history.state?.key,
      };
      return updatedHistory;
    });
    // Only run when location changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const trackNavigation = (to: string | number, options: { replace?: boolean } = {}) => {
    // Handle programmatic navigation and update our history tracking accordingly
    // ----------------------------------------------------------------------------------------

    if (typeof to !== "number") {
      // String path - we need to update our history tracking

      // (1) Skip tracking for external links
      if (to.startsWith("https://") || to.startsWith("http://")) {
        return;
      }

      // (2) Resolve relative paths to absolute paths
      const currentPath = appHistory[currentIndex]?.path ?? "/";
      const baseUrl = new URL(currentPath, window.location.origin);
      const resolvedUrl = new URL(to, baseUrl);
      const resolvedTo = resolvedUrl.pathname + resolvedUrl.search;

      // (4) Update history based on navigation type (push or replace)
      const newHistory = [...appHistory.slice(0, currentIndex + 1)];
      let newIndex = currentIndex;

      if (options.replace) {
        // Replace current entry
        newHistory[currentIndex] = { path: resolvedTo, key: null }; // Key will be updated after navigation
      } else {
        // Push new entry, truncating any forward history
        newHistory.push({ path: resolvedTo, key: null }); // Key will be updated after navigation
        newIndex = newHistory.length - 1;
      }

      // (5) Update state and persist to session storage
      setAppHistory(newHistory);
      setCurrentIndex(newIndex);
      sessionStorage.setItem(STORAGE_KEYS.APP_HISTORY, JSON.stringify(newHistory));
      sessionStorage.setItem(STORAGE_KEYS.CURRENT_INDEX, newIndex.toString());
    }
    // For numeric 'to' values, we let the browser handle navigation
    // and our popstate handler will update our state
  };

  // Navigation helper functions
  const goBack = useCallback(() => {
    if (currentIndex > 0) {
      navigate(-1); // Use React Router's navigate to go back
    }
  }, [navigate, currentIndex]);

  const goForward = useCallback(() => {
    if (currentIndex < appHistory.length - 1) {
      navigate(1); // Use React Router's navigate to go forward
    }
  }, [navigate, currentIndex, appHistory.length]);

  // Computed properties for navigation availability
  const canGoBack = currentIndex > 0;
  const canGoForward = currentIndex < appHistory.length - 1;

  // Prepare context value
  const contextValue = {
    trackNavigation,
    goBack,
    goForward,
    canGoBack,
    canGoForward,
  };

  return <NavigationContext.Provider value={contextValue}>{children}</NavigationContext.Provider>;
};

export const useUnsafeNavigationControls = (): NavigationContextValue | null => {
  // This hook provides access to navigation controls, but may return null during initialization
  // The consuming component must handle the null case appropriately
  return useContext(NavigationContext);
};
